// Comprehensive Virginia family law data based on Custody X Change and official Virginia sources
const virginiaData = {
  'virginia': {
    name: 'Virginia',
    overview: {
      keyPoints: [
        'Virginia follows the "best interests of the child" standard with 10 specific factors outlined in Virginia Code § 20-124.3',
        'Joint custody is not presumed - courts make individualized determinations based on evidence and circumstances',
        'Virginia uses traditional "custody" and "visitation" terminology rather than modern parenting time language',
        'The state requires detailed parenting plans in all contested custody cases involving minor children',
        'Virginia has both Circuit Court (divorce court) and Juvenile & Domestic Relations (J&DR) Court jurisdictions',
        'Relocation with children requires court approval or written consent from the other parent for moves over 100 miles',
        'Virginia courts emphasize stability and continuity in the child\'s living arrangements'
      ],
      uniqueFeatures: [
        'Virginia has dual court system - Circuit Court for divorce cases and J&DR Court for custody-only cases',
        'The state allows for informal custody agreements that can be formalized by the court',
        'Virginia has specific provisions for military families due to significant military presence',
        'The state recognizes both fault and no-fault grounds for divorce affecting custody decisions',
        'Virginia allows for custody investigations and evaluations by court-appointed professionals',
        'The state has specialized domestic violence court procedures with integrated family services',
        'Virginia requires mandatory mediation in some jurisdictions before contested hearings'
      ]
    },
    legalRequirements: {
      residency: 'Either parent or the child must have lived in Virginia for at least 6 months before filing, or Virginia must be the child\'s home state under UCCJEA',
      filing: 'File in Circuit Court for divorce cases or J&DR Court for custody-only cases in the county/city where either parent resides or where the child has lived',
      paternity: 'Unmarried fathers can establish paternity through voluntary acknowledgment, genetic testing, court order, or presumption of paternity',
      documentation: 'Required documents include petition/complaint, summons, financial statement, and proposed parenting plan in contested cases',
      uniqueRules: [
        'Virginia requires financial disclosure in all family law cases involving children or support',
        'Mediation is mandatory in some jurisdictions before contested custody hearings',
        'Guardian ad litem appointment is common in contested custody cases involving allegations of abuse',
        'Parent education programs are required in many jurisdictions for divorcing parents',
        'Virginia allows emergency custody orders for situations involving immediate child welfare concerns'
      ]
    },
    custodyTypes: {
      physicalCustody: 'Virginia uses "physical custody" to determine where the child primarily resides and who provides day-to-day care',
      legalCustody: 'Legal custody involves decision-making authority for major life decisions including education, healthcare, and religious upbringing',
      jointCustody: 'Joint custody (both legal and physical) requires court approval and demonstration that it serves the child\'s best interests',
      visitation: 'Called "visitation rights" - includes specific schedules for the non-custodial parent\'s time with the child',
      specialProvisions: [
        'Supervised visitation may be ordered when child safety or welfare concerns exist',
        'Reasonable visitation allows flexible scheduling between cooperative parents',
        'Extended summer visitation is standard to accommodate school schedules',
        'Holiday visitation typically alternates between parents on annual basis',
        'Virtual visitation (video calls) is increasingly recognized for long-distance and military situations',
        'Grandparent visitation rights are protected under Virginia Code § 20-124.1'
      ]
    },
    parentingPlans: {
      required: true,
      elements: [
        'Detailed custody and visitation schedule including regular rotation, weekends, and holidays',
        'Specific holiday and vacation schedules with clear alternating arrangements between parents',
        'Transportation arrangements and responsibility for travel costs between parents\' residences',
        'Communication methods between parents and guidelines for parent-child contact during visitation',
        'Decision-making authority allocation for education, healthcare, religious, and extracurricular activities',
        'Dispute resolution procedures including mediation requirements before court intervention',
        'Relocation provisions and notification requirements for moves affecting the child\'s residence',
        'Any special conditions, restrictions, or accommodations for either parent',
        'Provisions for child care during work hours and right of first refusal',
        'Guidelines for introducing new romantic partners to the children',
        'Emergency contact procedures and decision-making protocols'
      ],
      tips: [
        'Be extremely specific about pickup and drop-off times, locations, and procedures to avoid confusion',
        'Include detailed provisions for Virginia\'s varying geography from urban Northern Virginia to rural areas',
        'Address how to handle missed visitation due to weather, work schedules, traffic, or emergencies',
        'Include specific language about meeting locations and travel considerations across Virginia',
        'Specify how major decisions will be made and communicated between parents',
        'Address the child\'s participation in extracurricular activities and school events',
        'Include technology use guidelines and virtual visitation provisions for military families',
        'Plan for extended summer arrangements and accommodate both parents\' work schedules'
      ]
    },
    childSupport: {
      guidelines: 'Virginia uses a modified Income Shares Model based on both parents\' gross income with Virginia-specific guidelines',
      factors: [
        'Gross monthly income of both parents from all sources',
        'Number of children covered by the support order',
        'Cost of health insurance premiums for the children',
        'Childcare expenses necessary for employment or education',
        'Extraordinary medical expenses not covered by insurance',
        'Other court-ordered support obligations',
        'Visitation time and overnight stays with paying parent (if significant)',
        'Standard of living the child would have enjoyed if parents remained together',
        'Special needs of the child requiring additional financial support',
        'Military allowances and benefits for service members'
      ],
      calculation: 'Virginia Child Support Guidelines using Income Shares Model with state-specific support schedule',
      modifications: 'Material change in circumstances required, typically 25% change in support amount or significant change in income or circumstances',
      enforcement: 'Strong enforcement through Virginia Department of Social Services including wage garnishment, asset seizure, tax intercept, and license suspension',
      detailedInfo: {
        basicCalculation: {
          description: 'Virginia follows a modified Income Shares Model that estimates what parents would spend on their children if the family were intact, with adjustments for Virginia-specific economic factors.',
          steps: [
            'Calculate each parent\'s gross monthly income from all verifiable sources',
            'Determine allowable deductions including taxes, mandatory retirement, and existing support orders',
            'Calculate adjusted gross monthly income for each parent after allowable deductions',
            'Add both parents\' adjusted gross incomes to determine combined monthly income',
            'Use Virginia Child Support Guidelines Schedule to find basic support obligation for number of children',
            'Allocate support obligation proportionally based on each parent\'s percentage of combined income',
            'Add costs for health insurance, childcare, and extraordinary medical expenses',
            'Apply any adjustments for extended visitation time or special circumstances',
            'Calculate final monthly support amount payable by obligor parent'
          ]
        },
        incomeTypes: {
          included: [
            'Wages, salary, commissions, bonuses, and overtime pay',
            'Military pay including base pay, allowances, and special pay',
            'Self-employment income (gross receipts minus ordinary business expenses)',
            'Investment income, dividends, interest, and capital gains',
            'Rental property income (after reasonable expenses)',
            'Retirement benefits, pensions, and annuity payments',
            'Social Security benefits (including disability and survivor benefits)',
            'Unemployment compensation and workers\' compensation benefits',
            'Spousal support or maintenance received from previous relationships',
            'Trust income and regularly received gifts or inheritances',
            'Royalties, patents, and intellectual property income',
            'Business income from partnerships, corporations, or LLCs'
          ],
          excluded: [
            'Means-tested public assistance (TANF, SNAP, WIC, etc.)',
            'Foster care payments received for non-biological children',
            'Child support received for other children not subject to current order',
            'One-time gifts or inheritances (unless creating ongoing income)',
            'Student loans, grants, and educational financial aid',
            'Supplemental Security Income (SSI)',
            'Income of new spouse or cohabitating partner',
            'Reimbursement for job-related expenses',
            'Income from illegal activities'
          ]
        },
        deductions: {
          mandatory: [
            'Federal income taxes based on appropriate filing status',
            'Virginia state income taxes',
            'FICA taxes (Social Security and Medicare)',
            'Mandatory retirement contributions required by employer',
            'Union dues required as condition of employment',
            'Court-ordered child support for other children',
            'Court-ordered spousal support/maintenance to former spouse'
          ],
          optional: [
            'Health insurance premiums for the child (added separately to support)',
            'Childcare expenses necessary for employment or education',
            'Extraordinary medical expenses for the child (shared proportionally)'
          ]
        },
        visitationAdjustment: {
          description: 'Virginia allows adjustment to child support when the paying parent has significant overnight visitation time with the child.',
          thresholds: [
            'No adjustment for less than 25% overnight visitation (91 nights per year)',
            'Potential adjustment for 25-35% overnight visitation (91-127 nights per year)',
            'Standard adjustment consideration for 35%+ overnight visitation (128+ nights per year)',
            'Extended visitation adjustment may apply for equal or near-equal time arrangements'
          ],
          calculation: 'Adjustment reduces support obligation based on additional direct costs during extended visitation and reduced costs to custodial parent',
          factors: [
            'Actual overnight stays documented over previous 12 months',
            'Additional housing costs for accommodating child during visitation',
            'Duplicate expenses (clothing, toys, school supplies) in both homes',
            'Transportation costs for visitation exchanges',
            'Reduced household expenses for custodial parent during visitation periods'
          ]
        },
        additionalExpenses: {
          healthInsurance: {
            description: 'Cost of health insurance premiums specifically attributable to covering the child',
            allocation: 'Shared proportionally between parents based on their respective income percentages',
            requirements: 'Must be reasonable in cost, accessible to child, and provide adequate coverage',
            considerations: [
              'Premium cost must be specifically for child coverage, not family plan differential',
              'Insurance must be available through employer or Virginia health marketplace',
              'Coverage should include medical, dental, and vision if reasonably available',
              'Both parents should have access to insurance cards and provider information',
              'Special considerations for military TRICARE benefits'
            ]
          },
          childcare: {
            description: 'Necessary childcare costs directly related to employment, job training, or education',
            allocation: 'Shared proportionally between parents based on their respective income percentages',
            requirements: 'Must be work-related or education-related necessity for custodial parent',
            considerations: [
              'Childcare must be necessary for parent to work or attend school',
              'Costs should be reasonable for the local area and type of care',
              'Before and after school care is typically included',
              'Summer childcare during school breaks may be included',
              'Receipts and documentation required for verification'
            ]
          },
          extraordinaryMedical: {
            description: 'Uninsured medical, dental, and vision expenses exceeding $250 per year per child',
            examples: [
              'Dental treatment not covered by insurance (fillings, crowns, extractions)',
              'Orthodontic treatment and appliances',
              'Vision care including eye exams, glasses, and contact lenses',
              'Mental health counseling and therapy sessions',
              'Physical therapy and rehabilitation services',
              'Prescription medications not covered by insurance',
              'Medical equipment and supplies (braces, wheelchairs, etc.)',
              'Treatment for chronic conditions requiring ongoing care',
              'Emergency medical treatment and hospitalization',
              'Specialized medical care and consultations'
            ],
            allocation: 'Shared proportionally between parents based on their respective income percentages',
            procedure: 'Parent incurring expense should seek pre-approval for non-emergency expenses over $500'
          },
          extracurricular: {
            description: 'Costs for children\'s activities, sports, and enrichment programs',
            examples: [
              'Youth sports team fees, equipment, and travel costs',
              'Music lessons and instrument rental or purchase',
              'Dance, martial arts, gymnastics, or other activity classes',
              'Summer camps and recreational day programs',
              'School trips, field trips, and educational activities',
              'Tutoring and educational support services',
              'Art, theater, or other creative programs',
              'Religious education and confirmation programs'
            ],
            note: 'Not automatically included in basic support calculation - must be agreed upon by parents or specifically court-ordered',
            considerations: [
              'Activities should be in the child\'s best interest and age-appropriate',
              'Costs should be reasonable and within family\'s financial means',
              'Both parents should have input on major extracurricular decisions',
              'Transportation to activities should be considered in planning'
            ]
          }
        },
        modifications: {
          grounds: [
            'Material change in either parent\'s income (typically 25% or more)',
            'Significant change in visitation schedule or custody arrangement',
            'Change in health insurance costs or availability',
            'Change in childcare expenses or needs',
            'Child\'s needs change significantly (special needs, medical conditions)',
            'Emancipation, marriage, or death of a child',
            'Three years have passed since last order (allows for cost-of-living review)',
            'Change in number of children covered by order',
            'Military deployment or change in military status'
          ],
          process: [
            'File Petition for Modification of Child Support with supporting documentation',
            'Serve the other parent with petition and financial affidavit',
            'Provide updated income verification and expense documentation',
            'Attend court hearing or mediation as scheduled by court',
            'Present evidence supporting the requested modification',
            'Obtain modified court order with new support amount'
          ],
          timeline: 'Modifications are typically effective from the date of filing the petition, not the date of the final court order',
          documentation: [
            'Updated financial affidavit with current income and expenses',
            'Recent pay stubs, tax returns, and employment verification',
            'Documentation of changed circumstances (medical bills, job loss, etc.)',
            'Current childcare and health insurance costs',
            'Evidence of visitation schedule changes if applicable'
          ]
        },
        enforcement: {
          methods: [
            'Income withholding and automatic wage garnishment',
            'Interception of tax refunds (federal and state)',
            'Asset seizure including bank accounts and personal property',
            'Liens on real estate and personal property',
            'Suspension of driver\'s license and professional licenses',
            'Suspension of hunting, fishing, and recreational licenses',
            'Credit bureau reporting of delinquent support',
            'Contempt of court proceedings with possible jail time',
            'Passport denial for arrearages exceeding $2,500',
            'Interception of lottery winnings and gaming proceeds',
            'Garnishment of unemployment and workers\' compensation benefits'
          ],
          interest: 'Unpaid child support accrues interest at the rate of 6% per annum on past-due amounts',
          statute: 'No statute of limitations exists for collecting past-due child support - it remains collectible indefinitely',
          resources: [
            'Virginia Department of Social Services Division of Child Support Enforcement',
            'Local Commonwealth\'s Attorney offices for contempt proceedings',
            'Private collection agencies contracted by the state',
            'Interstate enforcement through other state agencies'
          ]
        }
      }
    },
    courtProcesses: {
      circuitCourt: {
        description: 'Circuit Court handles divorce cases where custody is part of the divorce proceedings',
        steps: [
          'File Complaint for Divorce including custody and support requests with required documents and filing fee',
          'Serve the other party with complaint, summons, and all required documents through proper legal service',
          'Defendant has 21 days to file Answer and Counterclaim if desired, or case may proceed as uncontested',
          'Discovery phase begins where both parties exchange financial information and relevant documents',
          'Attend mediation if required by local court rules or ordered by judge to attempt settlement',
          'Pre-trial conference with judge to narrow issues and discuss settlement possibilities',
          'Trial on contested issues where both parties present evidence, witnesses, and legal arguments',
          'Final decree entered incorporating custody, visitation, support, and property division orders'
        ],
        timeline: 'Typically 3-12 months depending on complexity and whether case is contested',
        jurisdiction: 'Has jurisdiction over divorce, custody, support, and property division in marriage dissolution cases'
      },
      jdrCourt: {
        description: 'Juvenile & Domestic Relations Court handles custody cases not involving divorce',
        steps: [
          'File Petition for Custody with supporting affidavits and required documentation',
          'Serve the other party with petition and summons through proper legal channels',
          'Preliminary hearing to establish temporary custody and support arrangements if needed',
          'Investigation or evaluation may be ordered by court to assess best interests of child',
          'Mediation session to attempt resolution of custody and visitation disputes',
          'Final hearing where both parties present evidence and testimony regarding custody arrangement'
        ],
        timeline: 'Typically 2-6 months depending on case complexity and court calendar',
        jurisdiction: 'Has jurisdiction over custody, visitation, support, and paternity cases not involving divorce'
      }
    },
    resources: {
      courts: [
        {
          name: 'Virginia Judicial System Family Law Resources',
          url: 'http://www.courts.state.va.us/courts/circuit/resources/family_law_resources.html',
          description: 'Official Virginia court system family division with forms, procedures, and self-help resources'
        },
        {
          name: 'Virginia Family Court Forms',
          url: 'http://www.courts.state.va.us/forms/circuit/family.html',
          description: 'Complete collection of Virginia family court forms for divorce, custody, and support cases'
        },
        {
          name: 'Virginia Self-Represented Litigant Resources',
          url: 'http://www.courts.state.va.us/courtadmin/aoc/djs/programs/pro_se/home.html',
          description: 'Self-help resources and assistance for people representing themselves in family court'
        },
        {
          name: 'Fairfax County Circuit Court Family Services',
          address: '4110 Chain Bridge Road, Fairfax, VA 22030',
          phone: '(703) 246-2221',
          description: 'Family court serving Northern Virginia including Fairfax County'
        },
        {
          name: 'Richmond City Circuit Court',
          address: '400 North 9th Street, Richmond, VA 23219',
          phone: '(804) 646-6505',
          description: 'Family court serving Richmond metropolitan area'
        },
        {
          name: 'Virginia Beach Circuit Court',
          address: '2425 Nimmo Parkway, Virginia Beach, VA 23456',
          phone: '(757) 385-4181',
          description: 'Family court serving Hampton Roads area including Virginia Beach'
        }
      ],
      support: [
        {
          name: 'Virginia Department of Social Services - Division of Child Support Enforcement',
          url: 'https://www.dss.virginia.gov/family/dcse/',
          phone: '1-800-468-8894',
          description: 'Official state agency for child support establishment, modification, and enforcement'
        },
        {
          name: 'Legal Aid Justice Center',
          url: 'https://www.justice4all.org/',
          phone: '(434) 977-0553',
          description: 'Free legal services for low-income families in family law matters statewide'
        },
        {
          name: 'Virginia State Bar Family Law Section',
          url: 'https://www.vsb.org/sections/family/',
          description: 'Professional resources and directory of family law attorneys in Virginia'
        },
        {
          name: 'Virginia Coalition for the Homeless - Legal Services',
          url: 'https://www.vch.org/',
          phone: '(804) 644-3685',
          description: 'Legal assistance for families facing housing and family law challenges'
        },
        {
          name: 'Virginia Fathers Rights Groups',
          url: 'https://www.facebook.com/groups/virginiafathersrights',
          description: 'Support networks and advocacy groups for fathers navigating family court in Virginia'
        }
      ],
      legal: [
        {
          name: 'Virginia State Bar Lawyer Referral Service',
          url: 'https://www.vsb.org/vlrs/',
          phone: '1-800-552-7977',
          description: 'Professional attorney referrals for family law cases throughout Virginia'
        },
        {
          name: 'University of Virginia School of Law Legal Clinic',
          url: 'https://www.law.virginia.edu/academics/clinics',
          description: 'Student-supervised legal services for qualifying family law matters'
        },
        {
          name: 'Washington and Lee University School of Law Legal Practice Clinic',
          url: 'https://law.wlu.edu/academics/experiential-learning/clinics',
          description: 'Clinical legal services including family law representation'
        },
        {
          name: 'Virginia Collaborative Law Professionals',
          url: 'https://www.collaborativelaw.org/find-a-professional/virginia/',
          description: 'Network of collaborative divorce attorneys and professionals in Virginia'
        },
        {
          name: 'Virginia Mediation Network',
          url: 'https://www.virginiamediationnetwork.org/',
          description: 'Family mediation services and dispute resolution professionals statewide'
        }
      ],
      educational: [
        {
          name: 'Children First Program Virginia',
          url: 'http://www.courts.state.va.us/programs/childrenfirst/home.html',
          description: 'Parent education program for divorcing parents with minor children (required in many jurisdictions)'
        },
        {
          name: 'Virginia Parenting Plan Resources',
          url: 'http://www.courts.state.va.us/courts/circuit/resources/parenting_plan_guide.html',
          description: 'Comprehensive guide to creating effective parenting plans in Virginia'
        },
        {
          name: 'Virginia Guardian ad Litem Program',
          url: 'http://www.courts.state.va.us/programs/gal/home.html',
          description: 'Information about guardian ad litem services for children in contested custody cases'
        },
        {
          name: 'Virginia Tech Extension - Family Life',
          url: 'https://ext.vt.edu/family/',
          description: 'Educational programs for co-parenting, family relationships, and child development'
        },
        {
          name: 'Virginia Parent Information and Resource Centers',
          url: 'https://www.doe.virginia.gov/special_ed/parents/parent_info_resource_ctrs/',
          description: 'Educational resources for parents navigating special needs and educational decisions'
        }
      ],
      filingInformation: {
        venues: [
          {
            name: 'Fairfax County Circuit Court',
            address: '4110 Chain Bridge Road, Fairfax, VA 22030',
            phone: '(703) 246-2221',
            jurisdiction: 'Fairfax County - Fairfax, Alexandria, McLean, and Northern Virginia suburbs'
          },
          {
            name: 'Richmond City Circuit Court',
            address: '400 North 9th Street, Richmond, VA 23219',
            phone: '(804) 646-6505',
            jurisdiction: 'Richmond metropolitan area including Henrico and Chesterfield Counties'
          },
          {
            name: 'Virginia Beach Circuit Court',
            address: '2425 Nimmo Parkway, Virginia Beach, VA 23456',
            phone: '(757) 385-4181',
            jurisdiction: 'Hampton Roads area including Virginia Beach, Norfolk, and Chesapeake'
          },
          {
            name: 'Loudoun County Circuit Court',
            address: '18 East Market Street, Leesburg, VA 20176',
            phone: '(703) 777-0270',
            jurisdiction: 'Loudoun County including Leesburg, Sterling, and western Northern Virginia'
          }
        ],
        filingFees: {
          'Divorce (with children)': '$86.00',
          'Divorce (without children)': '$86.00',
          'Custody Petition': '$25.00',
          'Child Support': '$25.00',
          'Modification': '$25.00',
          'Paternity': '$25.00',
          'Protective Order': 'No fee'
        },
        requiredForms: [
          'Complaint for Divorce or Petition for Custody',
          'Summons',
          'Financial Affidavit',
          'Parenting Plan (if children involved)',
          'Child Support Guidelines Worksheet',
          'Certificate of Service'
        ]
      },
      statutes: [
        {
          name: 'Virginia Code Title 20 - Domestic Relations',
          url: 'https://law.lis.virginia.gov/vacode/title20/',
          description: 'Primary statutes governing divorce, custody, and family law in Virginia'
        },
        {
          name: 'Virginia Child Support Guidelines (Va. Code § 20-108.2)',
          url: 'https://law.lis.virginia.gov/vacode/title20/chapter6/section20-108.2/',
          description: 'Official child support calculation guidelines and procedures'
        },
        {
          name: 'Virginia Custody and Visitation Statutes (Va. Code § 20-124.1 et seq.)',
          url: 'https://law.lis.virginia.gov/vacode/title20/chapter6/section20-124.1/',
          description: 'Comprehensive custody and visitation laws for Virginia'
        }
      ],
      emergencyContacts: [
        {
          name: 'Virginia Family Violence & Sexual Assault Hotline',
          phone: '1-800-838-8238',
          description: '24/7 statewide domestic violence crisis line and support services'
        },
        {
          name: 'Virginia Child Abuse Hotline',
          phone: '1-800-552-7096',
          description: '24/7 child abuse and neglect reporting hotline'
        },
        {
          name: 'Virginia Crisis Text Line',
          phone: 'Text CONNECT to 85511',
          description: '24/7 crisis support via text message'
        }
      ]
    },
    bestInterestsFactors: [
      'The age and physical and mental condition of the child, giving due consideration to the child\'s changing developmental needs',
      'The age and physical and mental condition of each parent',
      'The relationship existing between each parent and each child, giving due consideration to the positive involvement with the child\'s life, the ability to accurately assess and meet the emotional, intellectual and physical needs of the child',
      'The needs of the child, giving due consideration to other important relationships of the child, including but not limited to siblings, peers and extended family members',
      'The role that each parent has played and will play in the future, in the upbringing and care of the child',
      'The propensity of each parent to actively support the child\'s contact and relationship with the other parent, including whether a parent has unreasonably denied the other parent access to or visitation with the child',
      'The relative willingness and demonstrated ability of each parent to maintain a close and continuing relationship with the child, and the ability of each parent to cooperate in and resolve disputes regarding matters affecting the child',
      'The reasonable preference of the child, if the court deems the child to be of reasonable intelligence, understanding, age and experience to express such a preference',
      'Any history of family abuse as that term is defined in § 16.1-228 or sexual abuse',
      'Such other factors as the court deems necessary and proper to the determination'
    ],
    practicalTips: [
      'Virginia courts value detailed, specific parenting plans that minimize future disputes between parents',
      'Document all interactions and communications with the other parent using email or text when possible',
      'Understand whether your case should be filed in Circuit Court (divorce) or J&DR Court (custody only)',
      'Be prepared for mandatory mediation in many Virginia jurisdictions before contested hearings',
      'Keep detailed records of your time with your children and any expenses related to their care',
      'Follow all court orders precisely - violations can seriously impact future custody decisions',
      'Consider hiring a local attorney familiar with Virginia family court procedures and specific judges',
      'Military families should understand special provisions for deployment and military benefits'
    ]
  }
};

export default virginiaData;